import {
  ButtonHTMLAttributes,
  FC,
  type ReactElement,
  type MouseEvent,
} from 'react'
import classNames from 'classnames'
import {cva, type VariantProps} from 'class-variance-authority'

import {TooltipBody, TooltipWrapper} from 'components'

type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xlg'
type IconButtonPosition = 'top' | 'right' | 'bottom' | 'left'
type IconButtonBorderRadius = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const iconButtonVariants = cva('flex justify-center items-center', {
  variants: {
    size: {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xlg: 'w-14 h-14',
    },
    borderRadius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      full: 'rounded-full',
    },
    tooltipPlace: {
      top: 'top',
      right: 'right',
      bottom: 'bottom',
      left: 'left',
    },
  },
  defaultVariants: {
    size: 'md',
    borderRadius: 'full',
  },
})

export interface IconButtonProps
  extends Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      'onClick' | 'className'
    >,
    VariantProps<typeof iconButtonVariants> {
  children: ReactElement
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  buttonClasses?: string
  buttonDataCy: string
  showTooltip?: boolean
  tooltipText?: string
  tooltipId?: string
  size?: IconButtonSize
  tooltipPlace?: IconButtonPosition
  borderRadius?: IconButtonBorderRadius
}

export const IconButton: FC<IconButtonProps> = ({
  buttonDataCy,
  showTooltip = false,
  tooltipText,
  tooltipPlace = 'top',
  tooltipId = 'icon-button-tooltip',
  children,
  buttonClasses,
  size,
  borderRadius,
  onClick,
  ...rest
}) => {
  return (
    <>
      <TooltipWrapper id={tooltipId} variant="dark" place={tooltipPlace}>
        <TooltipBody>{tooltipText}</TooltipBody>
      </TooltipWrapper>
      <button
        {...rest}
        type={rest.type ?? 'button'}
        data-cy={buttonDataCy}
        data-tooltip-id={tooltipId}
        data-tooltip-hidden={!showTooltip}
        className={classNames(
          iconButtonVariants({size, borderRadius}),
          buttonClasses,
        )}
        onClick={onClick}
      >
        {children}
      </button>
    </>
  )
}
